// // ===================================
// // TOP NAVBAR COMPONENT
// // src/components/layout/TopNavbar.js
// // For authenticated app pages
// // ===================================

// import React from 'react';
// import { Link, useLocation } from 'react-router-dom';
// import { useAuth } from '../../context/AuthContext';
// import './TopNavbar.css';

// const TopNavbar = ({ onMenuClick, pageTitle }) => {
//   const { user } = useAuth();
//   const location = useLocation();

//   // Get page title from path if not provided
//   const getPageTitle = () => {
//     if (pageTitle) return pageTitle;
    
//     const path = location.pathname;
//     if (path.includes('/dashboard')) return 'Dashboard';
//     if (path.includes('/book')) return 'Book Session';
//     if (path.includes('/sessions')) return 'My Sessions';
//     if (path.includes('/progress')) return 'Progress';
//     if (path.includes('/interview')) return 'Interview';
//     if (path.includes('/start-session')) return 'Start Session';
//     if (path.includes('/settings')) return 'Settings';
//     if (path.includes('/payment-success')) return 'Payment Success';
//     return 'Career-Coach.AI';
//   };

//   return (
//     <header className="top-navbar">
//       <div className="top-navbar-left">
//         <button 
//           className="mobile-menu-btn"
//           onClick={onMenuClick}
//           aria-label="Toggle menu"
//         >
//           <span className="hamburger-line"></span>
//           <span className="hamburger-line"></span>
//           <span className="hamburger-line"></span>
//         </button>
        
//         <div className="page-title-section">
//           <h1 className="current-page-title">{getPageTitle()}</h1>
//           <div className="breadcrumb">
//             <Link to="/app/dashboard">Home</Link>
//             <span className="breadcrumb-separator">/</span>
//             <span>{getPageTitle()}</span>
//           </div>
//         </div>
//       </div>

//       <div className="top-navbar-right">
//         {/* Quick Actions */}
//         <div className="navbar-actions">
//           <Link 
//             to="/app/book" 
//             className="quick-book-btn"
//             title="Book a new session"
//           >
//             <span className="btn-icon">+</span>
//             <span className="btn-text">Book Session</span>
//           </Link>
//         </div>

//         {/* Notifications - placeholder for future */}
//         <button className="navbar-icon-btn" title="Notifications">
//           <span className="icon">🔔</span>
//           {/* <span className="notification-badge">3</span> */}
//         </button>

//         {/* Help */}
//         <button className="navbar-icon-btn" title="Help">
//           <span className="icon">❓</span>
//         </button>

//         {/* User Menu */}
//         <div className="user-menu">
//           <Link to="/app/settings" className="user-menu-trigger">
//             <div className="user-avatar-small">
//               {user?.name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
//             </div>
//             <div className="user-info-compact">
//               <span className="user-name-compact">{user?.name || user?.username}</span>
//             </div>
//           </Link>
//         </div>
//       </div>
//     </header>
//   );
// };

// export default TopNavbar;

// ===================================
// TOP NAVBAR COMPONENT - IMPROVED
// src/components/layout/TopNavbar.js
// Stage 11 Fix: Functional notifications & help
// ===================================

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './TopNavbar.css';

const TopNavbar = ({ onMenuClick }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const notificationRef = useRef(null);
  const helpRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (helpRef.current && !helpRef.current.contains(event.target)) {
        setShowHelp(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get current page title from path
  const getPageTitle = () => {
    const path = location.pathname;
    const titles = {
      '/app/dashboard': 'Dashboard',
      '/app/book': 'Book Session',
      '/app/sessions': 'My Sessions',
      '/app/progress': 'Progress',
      '/app/achievements': 'Achievements',
      '/app/settings': 'Settings',
      '/app/start-session': 'Start Session',
    };
    
    // Check for dynamic routes
    if (path.includes('/app/interview/')) return 'Interview';
    if (path.includes('/app/sessions/')) return 'Session Details';
    
    return titles[path] || 'Dashboard';
  };

  // Get breadcrumb
  const getBreadcrumb = () => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);
    
    if (parts.length <= 2) return null;
    
    return parts.slice(1).map((part, index) => {
      const isLast = index === parts.length - 2;
      const label = part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' ');
      
      return (
        <span key={part}>
          {index > 0 && <span className="breadcrumb-separator">/</span>}
          <span className={isLast ? 'breadcrumb-current' : 'breadcrumb-link'}>
            {label}
          </span>
        </span>
      );
    });
  };

  // Mock notifications - replace with real data later
  const notifications = [
    { id: 1, type: 'success', title: 'Session Completed', message: 'Your interview session was saved.', time: '2 hours ago' },
    { id: 2, type: 'info', title: 'New Achievement', message: 'You earned "First Session" badge!', time: '1 day ago' },
    { id: 3, type: 'reminder', title: 'Practice Reminder', message: 'Keep your skills sharp - book a session!', time: '3 days ago' },
  ];

  // Help items
  const helpItems = [
    { icon: '📖', label: 'Getting Started Guide', action: () => window.open('/help/getting-started', '_blank') },
    { icon: '💡', label: 'Interview Tips', action: () => window.open('/help/tips', '_blank') },
    { icon: '❓', label: 'FAQs', action: () => window.open('/help/faq', '_blank') },
    { icon: '💬', label: 'Contact Support', action: () => window.location.href = 'mailto:support@Career-Coach.AI' },
    { icon: '🐛', label: 'Report a Bug', action: () => alert('Bug report feature coming soon!') },
  ];

  return (
    <header className="top-navbar">
      {/* Left Section */}
      <div className="top-navbar-left">
        {/* Mobile Menu Button */}
        <button className="mobile-menu-btn" onClick={onMenuClick}>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Page Title */}
        <div className="page-title-section">
          <h1 className="current-page-title">{getPageTitle()}</h1>
          <div className="breadcrumb">
            <Link to="/app/dashboard" className="breadcrumb-link">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{getPageTitle()}</span>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="top-navbar-right">
        {/* Book Session Button */}
        <div className="navbar-cta">
          <Link to="/app/book" className="btn btn-primary btn-book">
            <span className="btn-icon">+</span>
            <span className="btn-text">Book Session</span>
          </Link>
        </div>

        {/* Notifications */}
        <div className="navbar-dropdown-container" ref={notificationRef}>
          <button 
            className={`navbar-icon-btn ${showNotifications ? 'active' : ''}`}
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowHelp(false);
            }}
            title="Notifications"
          >
            <span className="icon">🔔</span>
            {notifications.length > 0 && (
              <span className="notification-badge">{notifications.length}</span>
            )}
          </button>
          
          {showNotifications && (
            <div className="navbar-dropdown notifications-dropdown">
              <div className="dropdown-header">
                <h3>Notifications</h3>
                <button className="mark-all-read">Mark all read</button>
              </div>
              <div className="dropdown-content">
                {notifications.length > 0 ? (
                  notifications.map(notif => (
                    <div key={notif.id} className={`notification-item ${notif.type}`}>
                      <div className="notification-icon">
                        {notif.type === 'success' ? '✅' : notif.type === 'info' ? 'ℹ️' : '🔔'}
                      </div>
                      <div className="notification-content">
                        <span className="notification-title">{notif.title}</span>
                        <span className="notification-message">{notif.message}</span>
                        <span className="notification-time">{notif.time}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="dropdown-empty">
                    <span className="empty-icon">🔔</span>
                    <p>No new notifications</p>
                  </div>
                )}
              </div>
              <div className="dropdown-footer">
                <Link to="/app/notifications" onClick={() => setShowNotifications(false)}>
                  View All Notifications
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Help */}
        <div className="navbar-dropdown-container" ref={helpRef}>
          <button 
            className={`navbar-icon-btn ${showHelp ? 'active' : ''}`}
            onClick={() => {
              setShowHelp(!showHelp);
              setShowNotifications(false);
            }}
            title="Help & Support"
          >
            <span className="icon">❓</span>
          </button>
          
          {showHelp && (
            <div className="navbar-dropdown help-dropdown">
              <div className="dropdown-header">
                <h3>Help & Support</h3>
              </div>
              <div className="dropdown-content">
                {helpItems.map((item, index) => (
                  <button 
                    key={index} 
                    className="help-item"
                    onClick={() => {
                      item.action();
                      setShowHelp(false);
                    }}
                  >
                    <span className="help-icon">{item.icon}</span>
                    <span className="help-label">{item.label}</span>
                  </button>
                ))}
              </div>
              <div className="dropdown-footer help-footer">
                <p>Need more help?</p>
                <a href="mailto:support@Career-Coach.AI">support@Career-Coach.AI</a>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="user-menu">
          <Link to="/app/settings" className="user-menu-trigger">
            <div className="user-avatar-small">
              {user?.name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="user-info-compact">
              <span className="user-name-compact">{user?.name || user?.username}</span>
            </div>
          </Link>
        </div>
      </div>
    </header>
  );
};

export default TopNavbar;